import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useGetApprovalLimitGroupsQuery, useCreateApprovalLimitGroupMutation, useUpdateApprovalLimitGroupMutation } from '../../../../../api/endpoints/approvalLimitGroups';

import { FilterMatchMode } from 'primereact/api';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const useApprovalGroups = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItemId, setCurrentItemId] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [historySelectedItem, setHistorySelectedItem] = useState(null);

    const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
    const [apiFilters, setApiFilters] = useState({});
    const [filters, setFilters] = useState({});

    const filterFields = useMemo(() => [{ field: 'createdDate', headerName: th('common.createdDate'), type: 'date' }], [th]);

    const filterTypeMap = useMemo(
        () => ({
            createdDate: { type: 'date', matchMode: FilterMatchMode.DATE_IS }
        }),
        []
    );

    const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
    const initialFilters = useMemo(() => createInitialFilters(filterFieldNames, filterTypeMap), [filterFieldNames, filterTypeMap]);

    useEffect(() => setFilters(initialFilters), [initialFilters]);

    const {
        data: approvalGroupData,
        isLoading,
        refetch
    } = useGetApprovalLimitGroupsQuery({
        IsActive: 1,
        PageSize: lazyParams.rows,
        PageNumber: lazyParams.page,
        ...apiFilters
    });

    const [createItem] = useCreateApprovalLimitGroupMutation();
    const [updateItem] = useUpdateApprovalLimitGroupMutation();

    const resetForm = () => {
        setSelectedItem(null);
        setCurrentItemId(null);
        setEditMode(false);
        setSubmitted(false);
    };

    const openNewDialog = () => {
        resetForm();
        setDialogVisible(true);
    };

    const onSubmit = async (formData) => {
        setSubmitted(true);
        const { groupName, userIds } = formData;

        if (!groupName?.trim()) {
            console.log('❌ Validation failed:', { groupName });
            return;
        }

        // Format userIds as array of objects with id and name
        const processedUserIds = userIds ? userIds.map((user) => {
            if (typeof user === 'object' && user.id !== undefined) {
                return {
                    id: user.id,
                    name: user.name || ''
                };
            }
            // Fallback for direct ID values
            return {
                id: user,
                name: ''
            };
        }) : [];

        const payload = {
            groupName: groupName.trim(),
            isActive: 1,
            userIds: processedUserIds
        };

        try {
            if (editMode && currentItemId) {
                // Update existing item
                const updatePayload = {
                    id: currentItemId,
                    ...payload
                };
                await updateItem(updatePayload).unwrap();
                toast.current?.show({
                    severity: 'success',
                    summary: th('messages.updateSuccess'),
                    detail: th('approvalGroups.updateSuccessMessage'),
                    life: 3000
                });
            } else {
                await createItem(payload).unwrap();
                toast.current?.show({
                    severity: 'success',
                    summary: th('messages.saveSuccess'),
                    detail: th('approvalGroups.saveSuccessMessage'),
                    life: 3000
                });
            }
            setDialogVisible(false);
            resetForm();
            refetch();
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('messages.saveError'),
                life: 3000
            });
        }
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setCurrentItemId(item.id);
        setEditMode(true);
        setSubmitted(false);
        setDialogVisible(true);
    };

    const handleHistory = (item) => {
        setHistorySelectedItem(item);
        setHistoryDialogVisible(true);
    };

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(converted);

            const newApiFilters = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== '' && value !== null) {
                    let processedValue = value;

                    // Handle date fields
                    if (key === 'createdDate' && value instanceof Date) {
                        processedValue = value.toISOString();
                    }
                    if (key === 'isActive') {
                        processedValue = value ? 1 : 0;
                    }
                    
                    // API expects exact parameter names
                    if (key === 'createdDate') {
                        newApiFilters['CreatedDate'] = processedValue;
                    } else {
                        const pascal = key.charAt(0).toUpperCase() + key.slice(1);
                        newApiFilters[pascal] = processedValue;
                    }
                }
            });
            setApiFilters(newApiFilters);
            // Reset to first page when applying filters
            setLazyParams(prev => ({ ...prev, page: 1 }));
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setApiFilters({});
        // Reset to first page when resetting filters
        setLazyParams(prev => ({ ...prev, page: 1 }));
    };

    return {
        toast,
        dialogVisible,
        setDialogVisible,
        historyDialogVisible,
        setHistoryDialogVisible,
        historySelectedItem,
        openNewDialog,
        resetForm,
        filterFields,
        customFilterTypes: {},
        customFilterOptions: {},
        handleSearch: () => {},
        handleApplyFilters,
        handleResetFilters,

        tableProps: {
            data: (approvalGroupData?.items || []).map(item => ({
                ...item,
                usersName: item.approvalPersons?.map(person => person.userName).join(', ') || ''
            })),
            totalRecords: approvalGroupData?.totalCount || 0,
            isLoading,
            onPage: (e) => setLazyParams({ page: e.page + 1, rows: e.rows }),
            lazyParams: { first: (lazyParams.page - 1) * lazyParams.rows, rows: lazyParams.rows },
            onEdit: handleEdit,
            onHistory: handleHistory,
            resetPagination: false
        },
        formProps: {
            visible: dialogVisible,
            onSubmit,
            submitted,
            editMode,
            selectedItem
        }
    };
};

export default useApprovalGroups;
